import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, Layout, Text } from '../../../../design-system';
import { useSelector } from 'react-redux';
import PaymentsOptions from '../../VehicleOffersList/VehicleTile/PricingOptions/PaymentsOptions';
import {
  Wrapper,
  Title,
  DescriptionWrapper,
  VehicleNameWrapper,
  CTAGroup,
  ProductCardBottom,
} from './SlideContent.alpha.styles';
import { CopyTwo } from '../../Common/Common.styles';
import { COLORS } from '../../../styles/variables';
import { PromotionType } from '../../../types/offers';
import formatModelName from '../../../utils/formatModelName';
import removeYearFromModelName from '../../../utils/removeYearFromModelName';
import { priceFormat } from '../../../utils/formatPrice';
import { selectDealerContextId } from '../../../reducers/dealerContextReducer';
import Modal from '../../Modal';
import useCtaUrls from '../../../hooks/useCtaUrls';
import PaymentOptionsCa from '../../VehicleOffersList/VehicleTileCa/PaymentOptionsCa';
import { selectZipCode } from '../../../reducers/zipCodeReducer';
import { ALPHA_THEME } from '../../../../design-system/ThemeProvider/alphaTheme';
import { ProductCard, Tag, TextButton, TextLink } from '@oneaudi/unified-web-alpha-components';
import { parseDigitalLeadsData } from '../../../utils/digitalLeadsFormat';
import i18n from '../../../../i18n';

interface SlideContentProps {
  isActive?: boolean;
  offer: PromotionType;
  confirmedZipcode: string;
  tags?: React.ReactNode;
}

const SlideContentAlpha: React.FC<SlideContentProps> = ({
  offer,
  isActive,
  confirmedZipcode,
  tags,
}) => {
  const { t } = useTranslation();
  const isAlpha = !process.env.IS_LEGACY;
  const copyTwoColor = isAlpha ? ALPHA_THEME.textCopyDefault : COLORS.grey60;

  const {
    offerType,
    programToDate,
    customerDownPayment,
    monthlyPayment,
    programName,
    term,
    aprRate,
    offerModelDetail,
    programShortDescription,
  } = offer;

  const { modelYear, trimline, modelCodeDescription, modelName, dag } = offerModelDetail;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const isFrenchPage = i18n.language === 'fr';

  const inventoryUrl = useCtaUrls(offer);

  const isBonusOffers = offerType === 'credit';

  const { dealerId } = useSelector(selectDealerContextId);
  const trimmedModelName = isCaMarket
    ? modelName
    : removeYearFromModelName(modelYear, modelCodeDescription);
  const msrp = priceFormat(offer?.offerModelDetail?.msrp);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const expiryDate = `${t('promo.tile.offerEnd')} ${programToDate}`;

  const offerLabel = useMemo(() => {
    if (isCaMarket) return null;
    // if it's a stack offer use the program name of stack offer otherwise use program name from actual offer
    if (programName === 'AUD_SOA_COMBINED') return 'Season of Audi Bonus + Audi Credit';
    if (programName === 'AUD_SALES_EVENT') return 'Season of Audi Customer Bonus';
    if (programName === 'AUD_CUSTOMER_CREDIT') return 'Customer Bonus';
    if (programName === 'AUD_COSTCO_SELECT_PROGRAM') return 'Costco Select Bonus';
    if (programName === 'AUD_CONQUEST') return 'Conquest Bonus';
    if (programName === 'AUD_LOYALTY') return 'Loyalty Bonus';
    return 'Audi Credit';
  }, [programName, isCaMarket]);

  const modelNameToRender = isCaMarket ? modelName : formatModelName(modelCodeDescription);

  const digitalLeadsInfo = useMemo(() => {
    return parseDigitalLeadsData(offer, isCaMarket, confirmedZipcode, dealerId);
  }, [confirmedZipcode, dealerId, offer, isCaMarket]);

  const VehicleName = (
    <VehicleNameWrapper>
      <Headline
        data-testid="featured-offer-card-title"
        tag="h4"
        variant={{
          order: '4',
          type: 'headline',
          weight: 'bold',
        }}
      >
        {modelNameToRender}
      </Headline>
    </VehicleNameWrapper>
  );

  const TrimLineName = useMemo(() => {
    if (offer.offerType === 'credit') {
      return (
        <Title data-testid="featured-offer-card-bonus">
          {priceFormat(offer.creditBonus)} {offerLabel} bonus
        </Title>
      );
    }
    if (offer.offerType === 'lease' && trimline.toLocaleLowerCase() !== 'base') {
      return (
        <Text
          data-testid="featured-offer-card-trimline"
          variant={{
            order: '2',
            style: 'normal',
          }}
        >
          {trimline}
        </Text>
      );
    }
    return null;
  }, [offer.creditBonus, offer.offerType, offerLabel, trimline]);

  const Description = <CopyTwo color={copyTwoColor}>{programShortDescription}</CopyTwo>;

  const ShowDetailsCTA = useMemo(() => {
    return (
      <TextLink
        data-testid={isBonusOffers ? 'slide-cta-bonus-details' : 'slide-cta-offer-details'}
        onClick={() => setIsModalOpen(true)}
        tabIndex={isActive ? 0 : -1}
        aria-label={`view offer details ${offerType ?? ''}`}
        size="sm"
        variant="standalone"
      >
        <div>
          {isBonusOffers ? t('promo.offers.viewBonusOfferCta') : t('promo.offers.viewOffer')}
        </div>
      </TextLink>
    );
  }, [isActive, isBonusOffers, offerType, t]);

  return (
    <Wrapper
      data-promotion-type={offerType} // either lease, finane, or credit
      data-promotion-offerdate={programToDate} // validity date of offer
      data-promotion-credit={offerType === 'credit' ? offer.creditBonus : 'null'} // credit bonus offer
      data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'} // downpayment for lease offer
      data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'} // monthly payment for lease offer
      data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'} // term length for lease offer
      data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'} // apr for finance offer
      data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'} // termlength for finance offer
      data-vehicle-model-name={trimmedModelName} // name of model -excludes year // ??
      data-vehicle-model-year={modelYear} // year of model
      data-vehicle-price={msrp !== '-' ? msrp : 'null'} // price of vehicle ??
      data-vehicle-trim={trimline} // vehicle trim
      data-testid={`featured-offer-tile-${dag}`}
      isCaMarket={isCaMarket}
    >
      <ProductCard
        className="product-card"
        headline={{
          text: VehicleName,
        }}
        image={{
          alt: `${modelNameToRender} featured offer banner image`,
          fallbackSrc:
            'https://nar.media.audi.com/is/image/audinar/nemo/us/offers/fallbacks/FeaturedOffer1.png',
          sources: offer.featuredOfferImageUrl
            ? [{ src: offer.featuredOfferImageUrl, width: 560 }]
            : undefined,
          objectFit: 'cover',
        }}
        slot1={
          <div className={`product-card__content ${isCaMarket ? 'ca-market' : ''}`}>
            <Tag variant="read-only" size="md" label={tags} className="product-card__tag" />
            <div className="product-card__content__header">
              {isCaMarket ? (
                <PaymentOptionsCa
                  offer={offer}
                  isFeatured
                  showOfferDetails={() => setIsModalOpen(true)}
                />
              ) : (
                <PaymentsOptions offer={offer} labelColor={COLORS.grey60} hasSmallFont />
              )}
              <Text className={`product-card__content__trimname ${isCaMarket ? 'ca-market' : ''}`}>
                {offer.offerModelDetail.trimline}
              </Text>
            </div>
            {/* This one below has the wrong name, should be Customer Bonus Offer US */}
            {!isCaMarket && <Text>{TrimLineName}</Text>}
            {!isCaMarket && (
              <div className="product-card__content__info">
                {' '}
                <CopyTwo color={copyTwoColor}>{expiryDate}</CopyTwo>
                <Layout>{ShowDetailsCTA}</Layout>
              </div>
            )}
            <ProductCardBottom>
              <CTAGroup>
                <TextButton
                  variant="primary"
                  size="md"
                  href={inventoryUrl}
                  target="_blank"
                  icon="external-link"
                  iconPosition="trailing"
                  data-testid="featured-offer-inventory-cta"
                  className="featured-offer-button"
                >
                  {t('promo.offers.cta.inventory')}
                </TextButton>
                <TextButton
                  variant="secondary"
                  size="md"
                  type="button"
                  onClick={() => {}}
                  id="digital-leads"
                  data-info={digitalLeadsInfo}
                  data-testid="featured-offer-details-contact-cta"
                  className="featured-offer-button"
                >
                  {t('promo.offers.cta.contact')}
                </TextButton>
              </CTAGroup>
              {!isCaMarket ? (
                <DescriptionWrapper className="product-card__content__description">
                  {Description}
                </DescriptionWrapper>
              ) : (
                <Text className="product-card__content__description-ca-market">
                  {isFrenchPage ? offer.offerPlusFr : offer.offerPlusEn}
                </Text>
              )}
            </ProductCardBottom>
          </div>
        }
      ></ProductCard>
      {isModalOpen && (
        <Modal
          offer={offer}
          inventoryUrl={inventoryUrl}
          onCloseModal={handleCloseModal}
          dealerId={dealerId}
          isModal
        />
      )}
    </Wrapper>
  );
};

export default SlideContentAlpha;
